import { darken, lighten, Slide } from "@mui/material";
import Axios from "axios";
import { saveAs } from "file-saver";
import { getAuth } from "firebase/auth";
import { DateTime } from "luxon";
import { forwardRef } from "react";

import { authProvider } from "./models/auth";

import store from "./store";

const PATHS = [
  // 모든 주소 작성 필요
  "/",
  "/dashboard",
  "/dashboard/app",
  "/signin",
  "/register",
  "/profile",
  "/callbacks/mrlogin",
  "/terms",
  "/privacy",
  "/dtxa/privacy",
  "/dtxb/privacy",
  "/mochi/privacy"
];
const ROUTES = { Root: "/" };

PATHS.forEach((path) => {
  ROUTES[
    path
      .replace(/\//gi, "-")
      .split("-")
      .map((word) =>
        word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : ""
      )
      .join("")
  ] = path;
});

const axios = Axios.create({
  baseURL: `${process.env.REACT_APP_API_BASEURL}`,
});
axios.v = {};
const axiosVersions = {
  1.5: { baseURL: `${process.env.REACT_APP_NEW_API_BASEURL}` },
};
const setupInterceptors = (ax) => {
  ax.interceptors.request.use(
    async (config) => {
      // Do something before request is sent

      config.headers = {
        ...config.headers,
        ...(!config.forGuest ? await getAuthHeaders() : {}),
      };

      // config.withCredentials = true;

      return config;
    },
    (error) => {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  ax.interceptors.response.use(
    (response) => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    },
    async (error) => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      try {
        const { status, data } = error.response;

        // console.log(error.config);
        if (status === 401) {
          if (error.config.url === "/api/auth/token/refresh") {
            throw new Error();
          } else if (data.code === "ERR_JWT_EXPIRED") {
            await refreshAccessToken();

            return new Promise((resolve, reject) => {
              axios
                .request(error.config)
                .then((response) => {
                  resolve(response);
                })
                .catch((error) => {
                  reject(error);
                });
            });
          }
        }
      } catch (exception) {
        store.dispatch.auth.signout();
      }

      return Promise.reject(error);
    }
  );
};
setupInterceptors(axios);
Object.entries(axiosVersions).forEach(([k, v]) => {
  const instance = Axios.create(v);
  setupInterceptors(instance);
  axios.v[k] = instance;
});

export const regExps = {
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  CELLPHONE_KO: /^01([0|1|6|7|8|9]?)-?([0-9]{3,4})-?([0-9]{4})$/,
  CELLPHONE_KO_NUMBERONLY: /^01[016789]{1}\d{7,8}$/,
  PASSWORD: /^.*(?=^.{8,20}$)(?=.*[a-zA-Z])(?=.*\d).*$/,
};

export const consts = {
  ACCESS_TOKEN: "santa-access-token",
  LATEST_LOUNGES: "santa-studio-latest-lounges",
  SCREEN_CAPTURING: { breakpoint: 1400, width: 1320 },
};

export const getLocalDateTimeFromISO = (param) => {
  let raw = param instanceof Array ? param.raw[0] : param;

  // eslint-disable-next-line no-useless-escape
  const hasTimezone = raw.match(/[\+Z]/);

  return DateTime.fromISO(`${raw}${hasTimezone ? "" : "+09:00"}`).toLocal();
};

const hours = new Array(24).fill(null);
const minutes = ["00", "15", "30", "45"];
export const times = hours.reduce((acc, _, h) => {
  const amPm = h >= 12 ? "오후" : "오전";
  const hour = h % 12;
  const hLabel = `${hour > 0 ? hour : 12}`.padStart(2, "0");
  const hValue = `${h}`.padStart(2, "0");

  acc.push(
    ...minutes.map((m) => {
      return { label: `${amPm} ${hLabel}:${m}`, value: `${hValue}:${m}:00` };
    })
  );

  return acc;
}, []);

export const durationHours = [
  { label: "0시간", value: 0 },
  { label: "1시간", value: 1 },
  { label: "2시간", value: 2 },
  { label: "3시간", value: 3 },
  { label: "4시간", value: 4 },
  { label: "5시간", value: 5 },
  { label: "6시간", value: 6 },
];
export const durationMinutes = [
  { label: "00분", value: 0 },
  { label: "15분", value: 15 },
  { label: "30분", value: 30 },
  { label: "45분", value: 45 },
];

export const getRoundTime = (time) => {
  const sTimeValue = time.toFormat("HH:mm:ss");
  let sDateValue = time.toFormat("yyyy-MM-dd");

  let sHour = Number(sTimeValue.split(":")[0]);
  let sMin = Math.ceil(Number(sTimeValue.split(":")[1]) / 15) * 15;

  if (sMin === 60) {
    sHour += 1;
    sMin = 0;
  }

  const sHourValue = `${sHour === 24 ? 0 : sHour}`.padStart(2, "0");
  const sMinValue = `${sMin}`.padStart(2, "0");

  if (sHour === 24) {
    const nextDayDate = DateTime.fromISO(sDateValue).plus({
      days: 1,
    });
    sDateValue = nextDayDate.toFormat("yyyy-MM-dd");
  }

  return DateTime.fromISO(`${sDateValue}T${sHourValue}:${sMinValue}:00`);
};

// const getAuth = () => {
//   const params = new URLSearchParams();
//   params.append("redirectURL", document.location.href);
//   document.location.replace(
//     `${process.env.REACT_APP_CASTIO_BASEURL}/sign?${params.toString()}`
//   );
// };

export const refreshAccessToken = async () => {
  const session = await authProvider.getSession();
  const { data: reData } = await axios.post(
    `/api/auth/token/refresh`,
    { refreshToken: session.refreshToken },
    { forGuest: true }
  );
  // console.debug({ reData, reStatus });

  const { accessToken, refreshToken } = reData;
  authProvider.login({ accessToken, refreshToken });

  return accessToken;
};
const getAuthHeaders = async () => {
  const session = await authProvider.getSession();

  try {
    return {
      // Authorization: `Bearer ${await getAuth().currentUser.getIdToken(true)}`,
      Authorization: `Bearer ${session.accessToken}`,
    };
  } catch (err) {
    throw new Error("No session");
  }
};

const downloadDidicam = async () => {
  try {
    const { data, status } = await Axios(
      `https://devdidi.net/batch/downloadDidicam`
    );

    console.debug({ data, status });

    saveAs(data.aws_path);
  } catch (error) {
    alert(error.message);
  }
};

const kiloNumber = 1000;
const megaNumber = kiloNumber * kiloNumber;

const convertFileSize = (bytes, isForcedMBOnly, precision = 0) => {
  const mb = Number((bytes / megaNumber).toFixed(precision));

  if (isForcedMBOnly || mb < kiloNumber) {
    return `${mb}MB`;
  } else if (mb < megaNumber) {
    return `${Number((mb / kiloNumber).toFixed(2))}GB`;
  } else {
    return `${Number((mb / megaNumber).toFixed(2))}TB`;
  }
};

export {
  axios,
  ROUTES,
  getAuthHeaders,
  getAuth,
  downloadDidicam,
  convertFileSize,
};

export const topics = {
  HOSPITAL_REVIEW: "병원후기",
  ASKING: "질문할게요",
  SHARING_KNOWHOW: "노하우 공유",
  DECISIONS: "나의 다짐",
  COMMON_LIFE: "일상",
  MI_JAW_DISEASE_INFORMATION: "턱관절질환 정보",
  MI_DRK_DENTALSTORY: "김영균교수 치과이야기",
  MI_EDUCATIONS: "교육영상들"
};
export const topicEntries = Object.entries(topics);

export const Transition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const getBackgroundColor = (color, mode) =>
  mode === "dark" ? darken(color, 0.6) : lighten(color, 0.6);

export const getHoverBackgroundColor = (color, mode) =>
  mode === "dark" ? darken(color, 0.5) : lighten(color, 0.5);
